import { useEffect, useId, useState, type ReactNode } from 'react';
import type { Stream } from './api';
import logoSvg from './logo.svg?raw';

const PATHS: Record<string, ReactNode> = {
  streams: (
    <>
      <circle cx="8" cy="8" r="2" />
      <path d="M4.6 4.6a4.8 4.8 0 0 0 0 6.8M11.4 4.6a4.8 4.8 0 0 1 0 6.8" />
    </>
  ),
  usage: <path d="M3 13V8M8 13V3M13 13V6" />,
  key: (
    <>
      <circle cx="5.5" cy="10.5" r="2.5" />
      <path d="M7.3 8.7 13 3M11 5l1.5 1.5" />
    </>
  ),
  settings: (
    <>
      <path d="M3 5h10M3 11h10" />
      <circle cx="6" cy="5" r="1.6" fill="var(--bg)" />
      <circle cx="10" cy="11" r="1.6" fill="var(--bg)" />
    </>
  ),
  plus: <path d="M8 3v10M3 8h10" />,
  channel: (
    <>
      <rect x="2" y="4.5" width="12" height="8.5" rx="1.5" />
      <path d="M5.5 2 8 4.5 10.5 2" />
    </>
  ),
  webhook: <path d="M6 3.5a2.5 2.5 0 1 1 3 2.45L7 10M4.3 8.5a2.5 2.5 0 1 0 3.2 3.5h4.5M12 9.5a2.5 2.5 0 1 1-1.3 4.6" />,
  edit: <path d="M10.5 3.5 12.5 5.5 6 12H4v-2zM9.5 4.5l2 2" />,
  external: <path d="M6 3h7v7M13 3 5 11" />,
  info: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7.5v3.5M8 5v.2" />
    </>
  ),
  lock: (
    <>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </>
  ),
  video: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M6.8 6.2v3.6L9.8 8z" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="m2.5 4.5 5.5 4 5.5-4" />
    </>
  ),
};

export function Icon({ name, size = 16, width = 1.6 }: { name: keyof typeof PATHS | string; size?: number; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}

const STATUS_LABEL: Record<Stream['status'], string> = {
  live: 'Live',
  pending: 'Waiting',
  ended: 'Ended',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export function StatusBadge({ status, long }: { status: Stream['status']; long?: boolean }) {
  const label = long && status === 'pending' ? 'Waiting for signal' : status === 'live' && long ? 'LIVE' : STATUS_LABEL[status];
  return (
    <span className={`badge ${status}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

/** A small (i) that explains on hover or keyboard focus. Only where the explanation is really needed. */
/** Shown wherever a stream has no frame yet (or no longer has one). */
/** justON▶IR logo; "ON▶IR" takes the text colour. */
export function Logo() {
  return <span className="brand-logo" role="img" aria-label="JustOnAir" dangerouslySetInnerHTML={{ __html: logoSvg }} />;
}

export const THUMB_PLACEHOLDER = '/thumbnail-placeholder.jpg';

/**
 * The stream's newest frame, or the JustOnAir placeholder. Frames come once a
 * minute while live; the last one stays after the end, as long as the
 * recording is kept.
 */
export function Thumb({ stream, className = 'thumb' }: { stream: Stream; className?: string }) {
  const url = stream.status === 'pending' ? null : (stream.thumbnail?.url ?? null);
  const [failed, setFailed] = useState<string | null>(null);
  const src = url && failed !== url ? url : THUMB_PLACEHOLDER;
  return <img className={className} src={src} alt="" loading="lazy" decoding="async" onError={() => url && setFailed(url)} />;
}

export function Tip({ children, label = 'More information' }: { children: ReactNode; label?: string }) {
  const id = useId();
  return (
    <span className="tip">
      <button type="button" className="tip-btn" aria-label={label} aria-describedby={id}>
        <Icon name="info" size={13} />
      </button>
      <span role="tooltip" id={id} className="tip-body">
        {children}
      </span>
    </span>
  );
}

export function CopyField({ label, value, oneLine, secret, tip }: { label: string; value: string; oneLine?: boolean; secret?: boolean; tip?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard refused: the value is selectable anyway */
    }
  };
  return (
    <div className={`copy${secret ? ' secret' : ''}`}>
      <span className="label">
        {label}
        {tip && <Tip>{tip}</Tip>}
      </span>
      <div className="box">
        <span className={`val${oneLine ? ' one' : ''}`} title={oneLine ? value : undefined}>
          {value}
        </span>
        <button type="button" className="btn small" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/** Re-renders every `ms` so elapsed times tick. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function usd(v: string | number, digits = 2): string {
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toFixed(digits)}`;
}

export function bytes(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} KB`;
  return `${n} B`;
}

export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

export function when(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date(now);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

export function daysLeft(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 86_400_000));
}

export function kbps(k: number | null | undefined): string {
  if (k == null) return '—';
  return k >= 1000 ? `${(k / 1000).toFixed(1)} Mbps` : `${k} kbps`;
}

/** Why a stream ended, in words a developer understands. */
export function endReason(r: string | null): string {
  switch (r) {
    case null:
      return '';
    case 'deleted':
      return 'Ended from the dashboard or API';
    case 'disconnected':
      return 'Publisher left';
    case 'reservation_ttl':
      return 'Nothing connected within 30 minutes';
    case 'provision_failed':
      return 'No ingest capacity could be started';
    case 'duration_limit':
      return 'Reached the maximum duration';
    case 'limit_bitrate':
      return 'Over the 6 Mbps bitrate limit';
    case 'limit_resolution':
      return 'Over the declared resolution';
    case 'insufficient_credit':
      return 'Credit ran out';
    case 'simulcast_not_supported':
      return 'Simulcast is not supported';
    default:
      return r.replace(/_/g, ' ');
  }
}

export function recordingLine(s: Stream, now = Date.now()): string {
  const r = s.recording;
  if (!r) return s.record ? 'After it ends' : 'Off';
  switch (r.status) {
    case 'recording':
      return 'Recording';
    case 'processing':
      return 'Making the MP4…';
    case 'ready': {
      const left = daysLeft(r.delete_at, now);
      return `MP4 · ${bytes(r.bytes)}${left !== null ? ` · ${left} day${left === 1 ? '' : 's'} left` : ''}`;
    }
    case 'deleting':
      return 'Deleting';
    case 'deleted':
      return 'Deleted';
    case 'failed':
      return 'Failed';
    case 'not_recorded':
      return 'Off';
    case 'none':
      return 'None';
    default:
      return r.status;
  }
}

/**
 * What kind of account this is, when it is not the default: Partner (higher
 * limits) and Postpaid (may go below zero). Internal is shown only to our own
 * accounts. Prepaid new/trusted accounts show nothing.
 */
export function AccountBadges({ tier, billing }: { tier: string; billing: string }) {
  return (
    <>
      {tier === 'partner' && (
        <span className="badge acct partner" title="Partner limits: 50 live streams, 1080p, 24 h">
          Partner
        </span>
      )}
      {billing === 'postpaid' && (
        <span className="badge acct postpaid" title="Postpaid: the balance can go below zero; streams don't stop">
          Postpaid
        </span>
      )}
      {billing === 'internal' && (
        <span className="badge acct internal" title="Internal account: charged for the numbers, never stopped">
          Internal
        </span>
      )}
    </>
  );
}
